import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

import type { RootState, AppDispatch } from "../../redux/store";
import {
  listenToClassifiedGuestOrders,
  upsertClassifiedOrder,
} from "../../redux/classifiedOrdersSlice";

const ClassifiedDeliveries: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const orders = useSelector(
    (s: RootState) => s.classifiedOrders.orders
  );

  const listings = useSelector(
    (s: RootState) => s.classifieds.listings
  );

  useEffect(() => {
    if (userId) {
      dispatch(listenToClassifiedGuestOrders(userId));
    }
  }, [userId, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    listings.forEach((l) => (m[l.listingId] = l));
    return m;
  }, [listings]);

  // Flatten all items from all orders
  const deliveryItems = useMemo(() => {
    const items: any[] = [];

    orders.forEach((order) => {
      order.items?.forEach((item: any) => {
        items.push({
          ...item,
          orderId: order.id,
          guestId: order.guestId,
        });
      });
    });

    return items;
  }, [orders]);

  if (deliveryItems.length === 0) {
    return (
      <div style={styles.emptyWrap}>
        <h3>No deliveries found</h3>
        <button
          style={styles.primaryBtn}
          onClick={() => navigate("/shop")}
        >
          Order Products Now
        </button>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.grid}>
        {deliveryItems.map((item, index) => {
          const listing = listingsMap[item.listingId] || {};

          const variantPhoto =
            listing?.variants?.[item.colorId]?.imageUrl;

          const photo =
            variantPhoto ||
            listing?.photos?.[0] ||
            "/default-product.jpg";

          const status = item.deliveryStatus || {};

          const isFullyDelivered =
            status.sellerDelivered &&
            status.orderDelivered &&
            status.buyerReceived;

          let stage = "Confirmed";
          if (isFullyDelivered) stage = "Delivered";
          else if (status.sellerDelivered) stage = "Shipped";

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={styles.rectangleCard}
            >
              {/* LEFT SIDE */}
              <div style={styles.leftSection}>
                <div style={styles.productName}>
                  {listing?.name || item.name}
                </div>

                <div style={styles.badge(stage)}>
                  {stage}
                </div>

                {/* ACTIONS */}
                <div style={styles.actions}>
                  {!status.buyerReceived && (
                    <button
                      style={
                        status.sellerDelivered
                          ? styles.primaryMini
                          : styles.disabledMini
                      }
                      disabled={!status.sellerDelivered}
                      onClick={() =>
                        dispatch(
                          upsertClassifiedOrder({
                            paymentId: item.orderId,
                            listingId: item.listingId,
                            deliveryStatus: {
                              sellerDelivered: true,
                              buyerReceived: true,
                              orderDelivered: true,
                            },
                          })
                        )
                      }
                    >
                      Mark Received
                    </button>
                  )}

                  {isFullyDelivered && (
                    <button
                      style={styles.secondaryMini}
                      onClick={() =>
                        navigate(
                          `/ClassifiedProductsReview/${item.guestId}/${item.listingId}/${item.orderId}`
                        )
                      }
                    >
                      Review
                    </button>
                  )}
                </div>
              </div>

              {/* IMAGE RIGHT */}
              <div
                style={styles.imageWrap}
                onClick={() =>
                  navigate(
                    `/generalcommercedetails/${item.listingId}`
                  )
                }
              >
                <img
                  src={photo}
                  style={styles.image}
                  onError={(e) =>
                    ((e.target as HTMLImageElement).src =
                      "/default-product.jpg")
                  }
                />
              </div>

              {/* REORDER ICON */}
              <button
                style={styles.reorderBtn}
                onClick={() =>
                  navigate(
                    `/generalcommercedetails/${item.listingId}`
                  )
                }
              >
                ⟳
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ClassifiedDeliveries;



const styles: any = {
  page: {
    padding: "40px 20px",
  },

  grid: {
    display: "grid",
    gap: 20,
    gridTemplateColumns:
      "repeat(auto-fill, minmax(420px, 1fr))",
  },

  rectangleCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #eee",
    boxShadow: "0 6px 18px rgba(0,0,0,.08)",
    position: "relative",
  },

  leftSection: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    flex: 1,
  },

  productName: {
    fontSize: 16,
    fontWeight: 800,
  },

  imageWrap: {
    width: 95,
    height: 95,
    borderRadius: 14,
    overflow: "hidden",
    cursor: "pointer",
    marginLeft: 20,
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  badge: (stage: string) => ({
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 14,
    fontSize: 12,
    fontWeight: 700,
    width: "fit-content",
    background:
      stage === "Delivered"
        ? "#d6ffd8"
        : stage === "Shipped"
        ? "#fff4d6"
        : "#ffe5e5",
    color:
      stage === "Delivered"
        ? "#2e7d32"
        : stage === "Shipped"
        ? "#ef6c00"
        : "#c62828",
  }),

  actions: {
    display: "flex",
    gap: 8,
    marginTop: 6,
    flexWrap: "wrap",
  },

  primaryMini: {
    padding: "6px 12px",
    background: "#cc0000",
    color: "#fff",
    borderRadius: 8,
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
  },

  secondaryMini: {
    padding: "6px 12px",
    background: "#f1f1f1",
    borderRadius: 8,
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
  },

  disabledMini: {
    padding: "6px 12px",
    background: "#e0e0e0",
    color: "#999",
    borderRadius: 8,
    border: "none",
    cursor: "not-allowed",
  },

  reorderBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "#cc0000",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 34,
    height: 34,
    fontWeight: 900,
    cursor: "pointer",
  },

  primaryBtn: {
    padding: 14,
    background: "#cc0000",
    color: "#fff",
    borderRadius: 10,
    border: "none",
    fontWeight: 800,
    cursor: "pointer",
  },

  emptyWrap: {
    padding: 60,
    textAlign: "center",
  },
};
